import { Alert, Box, HStack, Image, Text, VStack } from "@chakra-ui/react"
import { LightMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import {
  FieldTip,
  TippedField
} from "@pathwright/ux/src/components/form/TippedField"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { Form, Formik } from "formik"
import { useEffect, useState } from "react"
import { toFormikValidate } from "zod-formik-adapter"
import { RegistrationRoleEnum } from "@pathwright/graphql-client/src/generated"
import {
  UseCohortsUpgradeInlineGateReturn,
  useCohortsUpgradeInlineGate
} from "../../cohort/create/UpgradeCohortsGate"
import BGMode from "../../components/BGMode"
import KeyboardFormSubmit from "../../components/formik/KeyboardFormSubmit"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import usePathCreatorFlowSubmit from "././usePathCreatorFlowSubmit"
import CohortSelector from "./CohortSelector"
import ResourceSelector from "./ResourceSelector"
import RoleSelector, { RoleSelectorOnChange } from "./RoleSelector"
import FormSubmitButton from "./components/FormSubmitButton"
import {
  PathCreatorRawValues,
  PathCreatorResult,
  PathCreatorValidValues,
  SelectedCohort,
  SelectedResource,
  isExistingItem,
  isNewItem,
  pathCreatorValidValuesSchema
} from "./state"
import { solid } from "./style"

const validator = toFormikValidate(pathCreatorValidValuesSchema)

// Exclude the null value from the PathCreatorRawValues type as the form does not allow null values.
type FormValues = Exclude<PathCreatorRawValues, null>

// Override user's role if we should force the editor role.
function useShouldForceDesignRole() {
  const { me } = usePathwrightContext()
  const shouldForceDesignRole = !!me.permissions?.can_edit_library
  return shouldForceDesignRole && false // For now, not forcing Design role.
}

function CohortsUpgradeGate({
  values,
  onChange
}: {
  values: FormValues
  onChange: (gate: UseCohortsUpgradeInlineGateReturn | null) => void
}) {
  // When user is attempting to create new Cohort for existing resource,
  // get the resource ID of the selected resource to determine permissions
  // to create Cohort.
  const selectedResourceId =
    isExistingItem(values.resource) && isNewItem(values.cohort)
      ? values.resource.id
      : null

  const cohortsUpgradeGate = useCohortsUpgradeInlineGate(selectedResourceId!)

  useEffect(() => {
    onChange(cohortsUpgradeGate)
  }, [!!cohortsUpgradeGate])

  if (!cohortsUpgradeGate) return null

  return (
    <Alert.Root
      status="info"
      variant="solid"
      borderRadius="20px"
      background="whiteAlpha.500"
      color="black"
      bg="whiteAlpha.600"
      backdropFilter="blur(20px)"
    >
      <Alert.Content>
        <HStack>
          <Pathicon
            icon={cohortsUpgradeGate.icon}
            style={{
              color: "black"
            }}
          />
          <Box>
            <Alert.Title>{cohortsUpgradeGate.heading}</Alert.Title>
            <Alert.Description
              color="inherit"
              css={{ "& > p": { color: "inherit !important" } }}
            >
              {cohortsUpgradeGate.body}
            </Alert.Description>
          </Box>
        </HStack>
      </Alert.Content>
    </Alert.Root>
  )
}

export type OnComplete = (state: PathCreatorResult) => Promise<void> | void

type PathCreatorFormProps = {
  targetUserId: number
  initialValue?: Exclude<FormValues, null>
  onComplete?: OnComplete
  // Whether the PathCreatorForm inputs should auto show their related tooltip when
  // user focuses the input.
  autoShowTips?: boolean
}

function PathCreatorForm({
  targetUserId,
  onComplete,
  initialValue = { resource: null, cohort: null },
  autoShowTips = false
}: PathCreatorFormProps) {
  const [error, setError] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cohortsUpgradeGate, setCohortsUpgradeGate] =
    useState<UseCohortsUpgradeInlineGateReturn | null>(null)

  const onFlowSubmit = usePathCreatorFlowSubmit()

  async function onSubmit(values: FormValues) {
    try {
      setIsSubmitting(true)
      const state = await onFlowSubmit({
        targetUserId,
        ...(values as PathCreatorValidValues)
      })

      // Call the onComplete callback.
      await onComplete?.(state)
    } catch (error) {
      setError(error as unknown as string)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Validate form based on expected action
  async function validate(values: FormValues) {
    // Check if the next state should be shown the cohorts upgrade gate.
    // Consider form invalid if so.
    if (cohortsUpgradeGate) {
      // NOTE: This error isn't displayed (neither is it intended to).
      return "Cannot create Cohort, please upgrade your plan."
    }

    return validator(values as PathCreatorValidValues)
  }

  const shouldForceDesignRole = useShouldForceDesignRole()

  const tipProps: FieldTip = {
    mode: autoShowTips ? "auto" : "manual",
    icon: "lightbulb",
    activeIcon: "lightbulb-on-fill",
    trigger: "click",
    popover: {
      positioning: { placement: "top-end" }
    }
  }

  return (
    <Formik
      initialValues={initialValue}
      // Necessary for keeping form up to date with pathCreatorState.
      // enableReinitialize
      validateOnMount
      // Prevent the default validation behavior on blur which can lead to validating
      // with stale data due to auto-focusing inputs on mount which must trigger a
      // validation call with stale data.
      validateOnBlur={false}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(form) => (
        <BGMode
          exclusions={[`[data-theme="dark"][id$="-listbox"]`]}
          input={{
            color: "whiteAlpha.800",
            // Attempt to override browser autofill styles, but not working.
            bg: "light-dark(transparent, transparent) !important",
            borderColor: "whiteAlpha.600",
            _placeholder: { color: "whiteAlpha.600" }
          }}
        >
          <Box as={Form} w="100%">
            <KeyboardFormSubmit />
            {!!form.values.resource &&
              !!(
                ("background_image" in form.values.resource
                  ? form.values.resource.background_image
                  : form.values.resource.image) || ""
              ) && (
                <Image
                  src={
                    ("background_image" in form.values.resource
                      ? form.values.resource.background_image
                      : form.values.resource.image) || ""
                  }
                  w="100%"
                  h="100%"
                  position="absolute"
                  inset={0}
                  borderRadius="xl"
                  zIndex={-1}
                  // fallback={
                  //   <Box
                  //     w="100%"
                  //     h="100%"
                  //     position="absolute"
                  //     inset={0}
                  //     borderRadius="xl"
                  //     {...transient}
                  //   />
                  // }
                />
              )}
            <VStack gap={2} {...solid}>
              <Box position="relative" w="100%" _focusWithin={{ zIndex: 1 }}>
                <TippedField
                  tip={{
                    ...tipProps,
                    placeholder: `E.g., "Web Design 101", "New Team Member", "Leadership Basics",...`,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          A <strong>Path</strong> is a series of steps to
                          completing a class, online course, team onboarding,
                          learning event, or any learning experience.{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/258211-start-designing-your-course-path"
                            target="_blank"
                            gap={0}
                            variant="underline"
                            color="inherit"
                          >
                            More about Paths
                            <Pathicon icon="chevron-right" />
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  as={ResourceSelector}
                  name="resource"
                  mode={
                    initialValue?.resource &&
                    "id" in initialValue?.resource &&
                    initialValue?.resource?.id === -1
                      ? "create"
                      : "select"
                  }
                  label="Path name"
                  hideLabel
                  onChange={async (item: SelectedResource | null) => {
                    // Store-acquired Paths have no source cohort; clear Design.
                    const resource =
                      item && !item.sourceCohort
                        ? { ...item, role: null }
                        : item
                    const values = {
                      resource,
                      cohort: form.values.cohort
                    }

                    // Clear out any "existing" cohort when changing resource.
                    if (
                      form.values.resource &&
                      "id" in form.values.resource &&
                      form.values.resource?.id !== item?.id &&
                      isExistingItem(form.values.cohort)
                    ) {
                      values.cohort = null
                    }

                    await form.setValues(values, true)
                  }}
                  disabled={isSubmitting}
                />
              </Box>
              <Box position="relative" w="100%" _focusWithin={{ zIndex: 1 }}>
                <TippedField
                  tip={{
                    ...tipProps,
                    placeholder: `E.g., "Spring 2025", "Anytime access", "New team members"...`,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          A <strong>Cohort</strong> is a group of people taking
                          a Path together. Cohorts can have unique discussions,
                          teachers, schedules, prices, and more. A Path can have
                          one or hundreds of Cohorts.{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/459249-cohorts-help-everyone-learn-together-again"
                            target="_blank"
                            textDecor="underline"
                            cursor="pointer"
                            gap={0}
                          >
                            Learn more
                            <Pathicon icon="chevron-right" />
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  as={CohortSelector}
                  name="cohort"
                  mode={
                    form?.values.resource &&
                    "id" in form?.values.resource &&
                    form?.values.resource?.id === -1
                      ? "create"
                      : "create-or-select"
                  }
                  label="Cohort name"
                  hideLabel
                  targetUserId={targetUserId}
                  selectedResourceId={
                    form.values.resource && "id" in form.values.resource
                      ? form.values.resource.id
                      : null
                  }
                  onChange={async (item: SelectedCohort) => {
                    // Preserve selected role.
                    await form.setValues(
                      {
                        resource: form.values.resource,
                        cohort: item
                          ? {
                              ...item,
                              role: form.values.cohort?.role || null
                            }
                          : null
                      },
                      true
                    )
                  }}
                  disabled={isSubmitting || !form.values.resource?.name}
                />
              </Box>

              <Box position="relative" w="100%" _focusWithin={{ zIndex: 1 }}>
                <TippedField
                  name="roles"
                  label="Your Roles"
                  hideLabel
                  as={RoleSelector}
                  tip={{
                    ...tipProps,
                    title: (
                      <>
                        <Text as="span" color="inherit">
                          Choose which roles you'd like to play in this Path{" "}
                          {">"} Cohort (you can change this anytime).{" "}
                          <Link
                            href="https://help.pathwright.com/en/articles/2083448-pathwright-member-roles"
                            target="_blank"
                            textDecor="underline"
                            cursor="pointer"
                            gap={0}
                            color="inherit"
                          >
                            Learn more about roles
                            <Pathicon icon="chevron-right" />
                          </Link>
                        </Text>
                      </>
                    )
                  }}
                  value={{
                    resourceRole: shouldForceDesignRole
                      ? RegistrationRoleEnum.Editor
                      : form.values.resource?.role,
                    cohortRole: form.values.cohort?.role
                  }}
                  onChange={async (
                    roles: Parameters<RoleSelectorOnChange>[0]
                  ) => {
                    const values = {
                      resource: {
                        ...form.values.resource!,
                        role: roles.resourceRole
                      },
                      cohort: {
                        ...form.values.cohort!,
                        role: roles.cohortRole
                      }
                    }
                    // Setting values without validating.
                    await form.setValues(values, true)
                  }}
                  atmosphericImage={form.values.resource?.image}
                  allowDesignRole={
                    // New Paths and owned Paths keep Design. Only hide it for
                    // existing store-acquired resources with no local source cohort.
                    !isExistingItem(form.values.resource) ||
                    !!form.values.resource.sourceCohort
                  }
                  disabled={isSubmitting || !form.values.cohort?.name}
                />
              </Box>

              <CohortsUpgradeGate
                values={form.values}
                onChange={setCohortsUpgradeGate}
              />

              {!!error && (
                <Alert.Root
                  status="error"
                  borderRadius="20px"
                  backdropFilter="blur(20px)"
                  colorPalette="red"
                >
                  <Alert.Content>
                    <HStack>
                      <Alert.Indicator>
                        <Pathicon
                          icon="caution-triangle"
                          style={{
                            color: "var(--alert-fg)"
                          }}
                        />
                      </Alert.Indicator>
                      <Box>
                        <Alert.Title>Error</Alert.Title>
                        <Alert.Description>
                          {error.toString()}
                        </Alert.Description>
                      </Box>
                    </HStack>
                  </Alert.Content>
                </Alert.Root>
              )}

              <LightMode>
                <HStack w="100%" justifyContent="center">
                  <FormSubmitButton
                    loading={isSubmitting}
                    disabled={!!cohortsUpgradeGate}
                    variant="subtle"
                  />
                </HStack>
              </LightMode>
            </VStack>
          </Box>
        </BGMode>
      )}
    </Formik>
  )
}

export default PathCreatorForm
